import {Customer, IItemContent} from "../../../typescript-library";
import {getShopId} from "../../lib/getShopId";
import {ItemContainerKeyType} from "./LazyCrossSelling";

export type PaReturnType = {
  payload?: string[];
  message?: string;
  code?: 0 | 1;
  type?: -1 | 1;
};

export type PaDataType = {
  paReturn: PaReturnType;
  namespace: ItemContainerKeyType;
  paUrl: string;
}

type ApiResultType = {result: IItemContent[]};

// Reference to the legacy code, where we have a cookie reader
declare const c: {
  sessionCookie: {
    get: (key: string) => string | false;
  };
};

type PatutilType = {paLocalStorage: {configData: {c: string}}};
declare global {
  interface Window {
    patutil: PatutilType | undefined;
  }
}

export const websiteIds: {[key: number]: string} = {
  100001: "c63a1643-49de-eb11-aae9-02dca44cceec",
  100009: "d3368e00-4ade-eb11-aae9-02dca44cceec",
  100017: "29d8b914-4ade-eb11-aae9-02dca44cceec",
  100027: "19e928a0-49de-eb11-aae9-02dca44cceec",
  100005: "a315dae0-49de-eb11-aae9-02dca44cceec",
  100020: "3f695d64-49de-eb11-aae9-02dca44cceec",
  100008: "6b691482-49de-eb11-aae9-02dca44cceec",
  100022: "59e7442d-4ade-eb11-aae9-02dca44cceec",
  100029: "d89451c1-49de-eb11-aae9-02dca44cceec",
};

export const paMainUrl = `https://recs-us-e1a.particularaudience.com/1.0/ClientRecom/${getWebsiteId()}/`;

export function getWebsiteId() {
  return websiteIds[getShopId()];
}

export function getPACustomerId() {
  try {
    // When customer rejects the cookies, there will be no `window.patutil`
    // All else cases would be an error on PA side
    return window.patutil?.paLocalStorage.configData.c;
  } catch (e) {
    const customerId = c.sessionCookie.get("PAC") || undefined;
    if (__DEVELOPMENT__) {
      /* eslint-disable no-console */
      console.log("DEVELOPMENT:");
      console.log("PAC:", customerId, ", typeof:", typeof customerId);
      console.log(e);
      /* eslint-enable no-console */
    }
    // No idea what this means, but maybe a chrome bug?
    if (customerId === "undefined") {
      return undefined;
    }

    return customerId;
  }
}

let headers: HeadersInit = {
  "Content-Type": "application/json",
};

// type NamespaceType = "BoughtTogether" | "SimilarProducts" | "Recommendations";

export async function retrievePAResult(
  paUrl: string,
  namespace: ItemContainerKeyType,
): Promise<PaDataType> {
  return await fetch(paUrl, {headers})
    .then(async (paResponse) => {
      return {
        paReturn: (await paResponse.json()) as PaReturnType,
        namespace,
        paUrl,
      };
    })
    .catch((paError: Error) => {
      // TODO Maybe sentry here
      if (__DEVELOPMENT__) {
        /* eslint-disable no-console */
        console.log("DEVELOPMENT:");
        console.log(paUrl);
        console.log(paError);
        /* eslint-enable no-console */
      }

      return {
        paReturn: {message: paError.message},
        namespace,
        paUrl,
      };
    });
}

export async function retrieveInternalResult(payload: string[], customer: Customer): Promise<ApiResultType> {
  const {customerGroupId, customerId} = customer;
  const customerParams = [`currencyCode=${window.c_ActiveCurrency}`];

  if (customerId) {
    customerParams.push(`customerId=${customerId}`);
  }

  if (customerGroupId) {
    customerParams.push(`customerGroupId=${customerGroupId}`);
  }

  const customerParamsString = customerParams.join("&");
  const itemIds = payload.join(",");
  const internalUrl = `${__API_URL__}/shop/${getShopId()}/items?view=content&itemIds=${itemIds}&${customerParamsString}`;

  if (customer.token) {
    headers = {
      ...headers,
      Authorization: `Bearer ${customer.token}`,
    };
  }

  try {
    return await fetch(internalUrl, {
      method: "GET",
      headers,
    }).then(async (resp) => {
      return (await resp.json()) as ApiResultType;
    });
  } catch (internalError) {
    // TODO Maybe sentry here
    if (__DEVELOPMENT__) {
      /* eslint-disable no-console */
      console.log("DEVELOPMENT:");
      console.log(internalUrl);
      console.log(internalError);
      /* eslint-enable no-console */
    }

    return {result: []} as ApiResultType;
  }
}

function sortInternalByPaOrder(ours: IItemContent[], paItemIds: string[]) {
  const sortedArray: IItemContent[] = [];
  // Sort items by the order of the itemIds in pa response
  for (const paItemId of paItemIds) {
    const itemIndex = ours.findIndex((item: IItemContent): boolean => `${item.itemId}` === paItemId);
    if (itemIndex > -1) {
      sortedArray.push(...ours.splice(itemIndex, 1));
    }
  }

  return sortedArray;
}

/**
 * Gets itemIds to show from PA, then gets the item data per itemIds from our GSS.
 * Item data is with customer related prices
 * @param paResult
 * @param customer
 * @param paUrl
 */
export async function retrieveItemContent(
  paResult: PaReturnType,
  customer: Customer,
  paUrl?: string,
): Promise<IItemContent[]> {
  if (!paResult.payload) {
    if (__DEVELOPMENT__) {
      // eslint-disable-next-line no-console
      console.log(`No PA response payload on '${paUrl}': ${paResult.message || paResult.code || "no message or code"}`);
    }

    return [];
  }

  const firstItemIds = paResult.payload.slice(0, 8);
  const internalResult = await retrieveInternalResult(paResult.payload, customer);

  return sortInternalByPaOrder(internalResult.result, firstItemIds);
}

export async function retrieveBoughtTogether(
  itemId: string,
): Promise<PaDataType> {
  // https://docs.particularaudience.com/api-definitions/collaborative-filtering-jaccard-items-bought-together
  let paUrl = `${paMainUrl}BoughtTogether?productRef=${itemId}&filterOutOfStock=false`;
  let paResult = await retrievePAResult(paUrl, "boughtTogether");
  if (!paResult.paReturn.payload || !paResult.paReturn.payload.length) {
    const paCustomerId = getPACustomerId();
    if (paCustomerId) {
      // https://docs.particularaudience.com/api-definitions/items-often-bought-together-recently-viewed-items
      paUrl = `${paMainUrl}BoughtTogetherRecentlyViewed?customerId=${paCustomerId}&filterOutOfStock=false`;
      paResult = await retrievePAResult(paUrl, "boughtTogether");
    }
  }

  return paResult;
}
